import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import Accordion from '../../../shared/Accordion';
import '../../../shared/StyledInput.css';
import { QUOTE_REMINDER_FIELDS } from '../../../shared/quotePriceReminder';

// Shipping Charge Rounding's steps — the backend's ROUNDING_STEPS
// (quoteShippingEstimateService.js).
const ROUNDING_STEPS = ['0.10', '0.50', '1.00', '5.00'];
// A stored step ('0.50', 0.5 or null) as the dropdown's value.
const roundingValue = (stored) => (stored === null || stored === undefined || stored === '' ? '' : Number(stored).toFixed(2));

// The Alerts table (issue #286): who each alert goes to when its business
// has no active Account Owner. Mirrors the backend's ALERTS
// (accountOwnerService.js); an alert left empty is "Same as default".
const DEFAULT_ALERT = { column: 'alert_default_user_id', label: 'Default' };
const ALERTS = [
  { column: 'alert_portal_order_user_id', label: 'Portal orders' },
  { column: 'alert_portal_quote_user_id', label: 'Portal quote decisions' },
  { column: 'alert_address_request_user_id', label: 'Delivery address requests' },
  { column: 'alert_trade_enquiry_user_id', label: 'Trade Enquiries', note: "Also becomes a new Lead's Account Owner." },
  { column: 'alert_website_enquiry_user_id', label: 'Website Enquiries', note: 'Also gets every website enquiry from a sender not yet in Optim.' },
];
const ALERT_COLUMNS = [DEFAULT_ALERT, ...ALERTS].map((a) => a.column);
const alertValues = (data) => Object.fromEntries(ALERT_COLUMNS.map((c) => [c, data[c] != null ? String(data[c]) : '']));

// Tenant-wide defaults for the Sales module. Backed by the same
// /api/platform-settings store as Platform Settings — these fields just
// live here in the UI since they're Sales/pricing-facing, not because the
// underlying storage is Sales-specific. Grouped into collapsible Accordion
// cards (default collapsed), same pattern as PlatformSettingsPage.js, now
// that the list has grown past one flat form.
function SalesSettingsPage({ onNavigate }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [backorderLeadDays, setBackorderLeadDays] = useState(30);
  const [savedBackorderLeadDays, setSavedBackorderLeadDays] = useState(30);
  const [newBadgeDays, setNewBadgeDays] = useState(60);
  const [savedNewBadgeDays, setSavedNewBadgeDays] = useState(60);
  const [availableSoonColor, setAvailableSoonColor] = useState('#8a5a00');
  const [savedAvailableSoonColor, setSavedAvailableSoonColor] = useState('#8a5a00');
  const [backorderNowColor, setBackorderNowColor] = useState('#5a3e00');
  const [savedBackorderNowColor, setSavedBackorderNowColor] = useState('#5a3e00');
  const [newBadgeColor, setNewBadgeColor] = useState('#1b5e20');
  const [savedNewBadgeColor, setSavedNewBadgeColor] = useState('#1b5e20');
  const [openingHoursStaleDays, setOpeningHoursStaleDays] = useState(90);
  const [savedOpeningHoursStaleDays, setSavedOpeningHoursStaleDays] = useState(90);
  const [addressStaleDays, setAddressStaleDays] = useState(90);
  const [savedAddressStaleDays, setSavedAddressStaleDays] = useState(90);
  const [quoteValidDays, setQuoteValidDays] = useState(30);
  const [savedQuoteValidDays, setSavedQuoteValidDays] = useState(30);
  const [priceReminder, setPriceReminder] = useState('');
  const priceReminderRef = useRef(null);
  const [savedPriceReminder, setSavedPriceReminder] = useState('');
  const [estimateWording, setEstimateWording] = useState('');
  const [savedEstimateWording, setSavedEstimateWording] = useState('');
  // '' for none, else the step as '0.50'.
  const [chargeRounding, setChargeRounding] = useState('');
  const [savedChargeRounding, setSavedChargeRounding] = useState('');
  const [comingUpThresholdDays, setComingUpThresholdDays] = useState(7);
  const [savedComingUpThresholdDays, setSavedComingUpThresholdDays] = useState(7);
  const [alertUsers, setAlertUsers] = useState(alertValues({}));
  const [savedAlertUsers, setSavedAlertUsers] = useState(alertValues({}));
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    axios.get('/api/platform-settings')
      .then((res) => {
        const data = res.data.data || {};
        const loadedBackorderLeadDays = data.backorder_lead_days ?? 30;
        setBackorderLeadDays(loadedBackorderLeadDays);
        setSavedBackorderLeadDays(loadedBackorderLeadDays);
        const loadedNewBadgeDays = data.new_badge_days ?? 60;
        setNewBadgeDays(loadedNewBadgeDays);
        setSavedNewBadgeDays(loadedNewBadgeDays);
        const loadedAvailableSoonColor = data.available_soon_badge_color ?? '#8a5a00';
        setAvailableSoonColor(loadedAvailableSoonColor);
        setSavedAvailableSoonColor(loadedAvailableSoonColor);
        const loadedBackorderNowColor = data.backorder_now_badge_color ?? '#5a3e00';
        setBackorderNowColor(loadedBackorderNowColor);
        setSavedBackorderNowColor(loadedBackorderNowColor);
        const loadedNewBadgeColor = data.new_badge_color ?? '#1b5e20';
        setNewBadgeColor(loadedNewBadgeColor);
        setSavedNewBadgeColor(loadedNewBadgeColor);
        const loadedOpeningHoursStaleDays = data.opening_hours_stale_days ?? 90;
        setOpeningHoursStaleDays(loadedOpeningHoursStaleDays);
        setSavedOpeningHoursStaleDays(loadedOpeningHoursStaleDays);
        const loadedAddressStaleDays = data.address_stale_days ?? 90;
        setAddressStaleDays(loadedAddressStaleDays);
        setSavedAddressStaleDays(loadedAddressStaleDays);
        const loadedQuoteValidDays = data.quote_valid_days ?? 30;
        setQuoteValidDays(loadedQuoteValidDays);
        setSavedQuoteValidDays(loadedQuoteValidDays);
        const loadedPriceReminder = data.quote_price_reminder ?? '';
        setPriceReminder(loadedPriceReminder);
        setSavedPriceReminder(loadedPriceReminder);
        const loadedEstimateWording = data.shipping_estimate_wording ?? 'Shipping is an estimate and will be confirmed at dispatch.';
        setEstimateWording(loadedEstimateWording);
        setSavedEstimateWording(loadedEstimateWording);
        const loadedChargeRounding = roundingValue(data.shipping_charge_rounding);
        setChargeRounding(loadedChargeRounding);
        setSavedChargeRounding(loadedChargeRounding);
        const loadedComingUpThresholdDays = data.coming_up_threshold_days ?? 7;
        setComingUpThresholdDays(loadedComingUpThresholdDays);
        setSavedComingUpThresholdDays(loadedComingUpThresholdDays);
        setAlertUsers(alertValues(data));
        setSavedAlertUsers(alertValues(data));
      })
      .catch((err) => showError(err, 'Failed to load Sales Settings'))
      .finally(() => setLoading(false));
    axios.get('/api/users/account-owners')
      .then((res) => setOwners(res.data.data || []))
      .catch((err) => showError(err, 'Failed to load staff for Alerts'));
    // eslint-disable-next-line
  }, []);

  // Drops {key} into the Quote Price Reminder at the cursor (or the end).
  const insertReminderField = (key) => {
    const el = priceReminderRef.current;
    const token = `{${key}}`;
    const start = el ? el.selectionStart : priceReminder.length;
    const end = el ? el.selectionEnd : priceReminder.length;
    setPriceReminder(priceReminder.slice(0, start) + token + priceReminder.slice(end));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess('');
    try {
      const res = await axios.put('/api/platform-settings', {
        backorder_lead_days: Number(backorderLeadDays),
        new_badge_days: Number(newBadgeDays),
        available_soon_badge_color: availableSoonColor,
        backorder_now_badge_color: backorderNowColor,
        new_badge_color: newBadgeColor,
        opening_hours_stale_days: Number(openingHoursStaleDays),
        address_stale_days: Number(addressStaleDays),
        quote_valid_days: Number(quoteValidDays),
        quote_price_reminder: priceReminder,
        shipping_estimate_wording: estimateWording,
        shipping_charge_rounding: chargeRounding === '' ? null : Number(chargeRounding),
        coming_up_threshold_days: Number(comingUpThresholdDays),
        ...Object.fromEntries(ALERT_COLUMNS.map((c) => [c, alertUsers[c] === '' ? null : Number(alertUsers[c])])),
      });
      setAlertUsers(alertValues(res.data.data || {}));
      setSavedAlertUsers(alertValues(res.data.data || {}));
      setSavedBackorderLeadDays(backorderLeadDays);
      setSavedNewBadgeDays(newBadgeDays);
      setSavedAvailableSoonColor(availableSoonColor);
      setSavedBackorderNowColor(backorderNowColor);
      setSavedNewBadgeColor(newBadgeColor);
      setSavedOpeningHoursStaleDays(openingHoursStaleDays);
      setSavedAddressStaleDays(addressStaleDays);
      setSavedQuoteValidDays(quoteValidDays);
      setPriceReminder(res.data.data?.quote_price_reminder ?? priceReminder);
      setSavedPriceReminder(res.data.data?.quote_price_reminder ?? priceReminder);
      setEstimateWording(res.data.data?.shipping_estimate_wording ?? estimateWording);
      setSavedEstimateWording(res.data.data?.shipping_estimate_wording ?? estimateWording);
      setSavedChargeRounding(chargeRounding);
      setSavedComingUpThresholdDays(comingUpThresholdDays);
      setSuccess('Sales settings saved.');
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setBackorderLeadDays(savedBackorderLeadDays);
    setNewBadgeDays(savedNewBadgeDays);
    setAvailableSoonColor(savedAvailableSoonColor);
    setBackorderNowColor(savedBackorderNowColor);
    setNewBadgeColor(savedNewBadgeColor);
    setOpeningHoursStaleDays(savedOpeningHoursStaleDays);
    setAddressStaleDays(savedAddressStaleDays);
    setQuoteValidDays(savedQuoteValidDays);
    setPriceReminder(savedPriceReminder);
    setEstimateWording(savedEstimateWording);
    setChargeRounding(savedChargeRounding);
    setComingUpThresholdDays(savedComingUpThresholdDays);
    setAlertUsers(savedAlertUsers);
    setSuccess('');
    if (onNavigate) onNavigate('sales-dashboard');
  };

  if (loading) {
    return <div style={{ width: '100%', maxWidth: 600, margin: '32px auto' }}>Loading...</div>;
  }

  return (
    <div style={{ width: '100%', maxWidth: 600, margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 }}>
      <h2 style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 20 }}>Sales Settings</h2>
      <p style={{ color: '#666', fontSize: 14, marginTop: 0 }}>Tenant-wide defaults for the Sales module.</p>

      <form onSubmit={handleSave} className="sales-settings-form" style={{ marginTop: 16 }}>
        <Accordion title="Product Availability" defaultOpen={false}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
            Defaults for a Product's backorder → available lifecycle and its evolving
            availability badge, driven by that Product's own Availability Date. A
            Product is visible with a badge from the moment its Availability Date is
            set, however far out — Backorder Lead Time only controls when the badge
            switches from "From &lt;date&gt;" to "Pre-order". A Product can
            override its own lifecycle state or hide either badge stage on its own
            record.
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontSize: 13 }}>Backorder Lead Time (days)</label><br />
              <input
                type="number"
                min="0"
                value={backorderLeadDays}
                onChange={(e) => setBackorderLeadDays(e.target.value)}
                style={{ width: 100 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 13 }}>Hide "New" Badge After (days)</label><br />
              <input
                type="number"
                min="0"
                value={newBadgeDays}
                onChange={(e) => setNewBadgeDays(e.target.value)}
                style={{ width: 100 }}
              />
            </div>
          </div>
        </Accordion>

        <Accordion title="Badge Colours" defaultOpen={false}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
            Accent colour for each stage of the availability badge — the pill background
            is a soft tint of this colour, this colour itself is the text.
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontSize: 13 }}>"From &lt;date&gt;"</label><br />
              <input
                type="color"
                value={availableSoonColor}
                onChange={(e) => setAvailableSoonColor(e.target.value)}
                style={{ width: 60, height: 32 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 13 }}>"Pre-order" / "Backorder"</label><br />
              <input
                type="color"
                value={backorderNowColor}
                onChange={(e) => setBackorderNowColor(e.target.value)}
                style={{ width: 60, height: 32 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 13 }}>"New"</label><br />
              <input
                type="color"
                value={newBadgeColor}
                onChange={(e) => setNewBadgeColor(e.target.value)}
                style={{ width: 60, height: 32 }}
              />
            </div>
          </div>
        </Accordion>

        <Accordion title="Opening Hours" defaultOpen={false}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
            A Lead/Customer whose Opening Hours haven't been checked within this many days shows
            a stale badge on the Prospects & Customers list and counts toward the Sales Dashboard's card.
          </div>
          <label style={{ fontSize: 13 }}>Staleness Threshold (days)</label><br />
          <input
            type="number"
            min="0"
            value={openingHoursStaleDays}
            onChange={(e) => setOpeningHoursStaleDays(e.target.value)}
            style={{ width: 100 }}
          />
        </Accordion>

        <Accordion title="Address" defaultOpen={false}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
            A Lead/Customer with no address on file, or whose Primary Address hasn't been added/
            edited within this many days, shows a stale badge on the Prospects & Customers list and
            counts toward the Sales Dashboard's card.
          </div>
          <label style={{ fontSize: 13 }}>Staleness Threshold (days)</label><br />
          <input
            type="number"
            min="0"
            value={addressStaleDays}
            onChange={(e) => setAddressStaleDays(e.target.value)}
            style={{ width: 100 }}
          />
        </Accordion>

        <Accordion title="Quotes" defaultOpen={false}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
            How many days out a Quote's Valid Until date is set when it's Sent. Locked onto
            that Quote at that moment — changing this only affects Quotes sent from then on.
          </div>
          <label style={{ fontSize: 13 }}>Quotes Valid For (days)</label><br />
          <input
            type="number"
            min="1"
            value={quoteValidDays}
            onChange={(e) => setQuoteValidDays(e.target.value)}
            style={{ width: 100 }}
          />
          <div style={{ fontSize: 12, color: '#888', margin: '16px 0 8px' }}>
            The first Note on every Quote, on screen and in print. Leave blank to show none.
          </div>
          <label style={{ fontSize: 13 }}>Quote Price Reminder</label><br />
          <textarea
            className="styled-input"
            ref={priceReminderRef}
            value={priceReminder}
            onChange={(e) => setPriceReminder(e.target.value)}
            rows={3}
            maxLength={1000}
            style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: 14 }}
          />
          <div style={{ fontSize: 12, color: '#888', marginTop: 6, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            Insert a field:
            {QUOTE_REMINDER_FIELDS.map((f) => (
              <span key={f.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <button
                  type="button"
                  title={f.label}
                  onClick={() => insertReminderField(f.key)}
                  style={{ fontFamily: 'monospace', fontSize: 12, padding: '2px 8px', borderRadius: 12, border: '1px solid #ccc', background: '#f5f5f5', cursor: 'pointer' }}
                >
                  {`{${f.key}}`}
                </button>
                {f.label}
              </span>
            ))}
          </div>
        </Accordion>

        <Accordion title="Shipping" defaultOpen={false}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
            Estimate Shipping rounds a Quote's Shipping charge up to this step, inc GST
            (ex GST for a GST-free Customer). The difference shows as Rounding in the
            Shipping Estimate.
          </div>
          <label style={{ fontSize: 13 }}>Shipping Charge Rounding</label><br />
          <select
            className="styled-input"
            value={chargeRounding}
            onChange={(e) => setChargeRounding(e.target.value)}
            style={{ width: 140 }}
          >
            <option value="">None</option>
            {ROUNDING_STEPS.map((step) => <option key={step} value={step}>${step}</option>)}
          </select>
          <div style={{ fontSize: 12, color: '#888', margin: '16px 0 8px' }}>
            Printed on a Quote whose shipping charge came from Estimate Shipping, even
            after staff type over it. Leave blank to print no estimate note.
          </div>
          <label style={{ fontSize: 13 }}>Shipping Estimate Wording</label><br />
          <textarea
            className="styled-input"
            value={estimateWording}
            onChange={(e) => setEstimateWording(e.target.value)}
            rows={2}
            maxLength={500}
            style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: 14 }}
          />
        </Accordion>

        <Accordion title="Pipeline" defaultOpen={false}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
            How soon a Pipeline card's pending Task due date or scheduled
            Meeting date counts as "Coming up" (Yellow) rather than "Not due yet"
            (Red) on its status dot.
          </div>
          <label style={{ fontSize: 13 }}>"Coming Up" Threshold (days)</label><br />
          <input
            type="number"
            min="1"
            value={comingUpThresholdDays}
            onChange={(e) => setComingUpThresholdDays(e.target.value)}
            style={{ width: 100 }}
          />
        </Accordion>

        <Accordion title="Alerts" defaultOpen={false}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
            Alerts go to the business&apos;s Account Owner. When it has no Account Owner, or the
            owner is switched off, they go to the staff member set here. Left empty, the email goes
            to the company email on Company Settings with a reminder to set someone here, and the
            alert&apos;s to-do is unassigned; portal orders still show under Awaiting acceptance
            (portal) on the Sales Orders list.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7' }}>Alert</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7' }}>When there&apos;s no Account Owner</th>
              </tr>
            </thead>
            <tbody>
              {[DEFAULT_ALERT, ...ALERTS].map((alert) => {
                const value = alertUsers[alert.column];
                const isDefault = alert === DEFAULT_ALERT;
                const known = value === '' || owners.some((o) => String(o.id) === value);
                return (
                  <tr key={alert.column}>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #e3e6ea' }}>
                      {isDefault ? <strong>{alert.label}</strong> : alert.label}
                      {alert.note && <div style={{ fontSize: 12, color: '#888' }}>{alert.note}</div>}
                    </td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #e3e6ea' }}>
                      <select
                        className="styled-input"
                        value={value}
                        onChange={(e) => setAlertUsers((prev) => ({ ...prev, [alert.column]: e.target.value }))}
                        aria-label={`${alert.label} alert staff member`}
                        style={{ minWidth: 200 }}
                      >
                        <option value="">{isDefault ? '(nobody)' : 'Same as default'}</option>
                        {!known && <option value={value}>(no longer available)</option>}
                        {owners.map((o) => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Accordion>

        {success && <div style={{ color: '#2e7d32', marginTop: 10 }}>{success}</div>}

        <div style={{ marginTop: 16 }}>
          <SaveButton loading={saving} />
          <CancelButton onClick={handleCancel} disabled={saving} style={{ marginLeft: 8 }} />
        </div>
      </form>
    </div>
  );
}

export default SalesSettingsPage;
